import { Spinner } from '@frontend/components/common';
import { LuCheck } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import PlusMinusNumberInput from '../../../components/plus-minus-number-input/plus-minus-number-input.component';
import WizardScreenContainer from '../../components/wizard-screen-container/wizard-screen-container.component';
import { ProductReview, useReviewRecycles } from './review-recycles.controller';

const ID = 'review-recycles';

const ProductReviewRow = (props: { product: ProductReview; onUpdate: (count: number) => void }) => {
    const { product, onUpdate } = props;

    const isModified = product.actualCount !== product.expectedCount;

    return (
        <div
            id={ID}
            className='bg-background-surface border-border-default flex flex-col gap-3 rounded-xl border p-4'>
            <div className='flex items-center justify-between'>
                <div className='flex flex-col gap-1'>
                    <span className='text-foreground-default text-base font-semibold'>{product.productName}</span>
                    {product.attributes.length > 0 && (
                        <div className='flex flex-wrap gap-1'>
                            {product.attributes.map((attr) => (
                                <div
                                    key={attr.attributeName}
                                    className='bg-border-default inline-flex h-6 min-w-6 items-center justify-center gap-1 rounded px-1'>
                                    <span className='text-foreground-default text-sm'>{attr.attributeName}:</span>
                                    <span className='text-foreground-default text-sm'>{attr.valueName}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <span className={`text-xs ${isModified ? 'text-status-danger' : 'text-foreground-secondary'}`}>
                    <FormattedMessage
                        id='kiosk.review-recycles.expected'
                        defaultMessage='Expected: {count}'
                        values={{ count: product.expectedCount }}
                    />
                </span>
            </div>
            <PlusMinusNumberInput
                value={product.actualCount}
                onChange={onUpdate}
                min={0}
            />
        </div>
    );
};

const ReviewRecycles = () => {
    const viewProps = useReviewRecycles();

    return (
        <WizardScreenContainer
            id={ID}
            header={{
                title: (
                    <FormattedMessage
                        id='kiosk.review-recycles.title'
                        defaultMessage='Review recycles'
                    />
                ),
                onBack: viewProps.onBack
            }}
            loadingModal={{ show: viewProps.isSubmitting }}
            footer={{
                onBack: viewProps.onBack,
                onSubmit: viewProps.onSubmit,
                submitDisabled: viewProps.isLoading || viewProps.products.length === 0 || viewProps.isSubmitting
            }}>
            <div className='flex size-full flex-col items-center justify-start overflow-hidden'>
                <div className='flex size-full flex-col overflow-y-auto'>
                    {viewProps.isLoading && (
                        <div className='flex size-full items-center justify-center'>
                            <Spinner />
                        </div>
                    )}

                    {!viewProps.isLoading && (
                        <div className='flex flex-col gap-3'>
                            {viewProps.products.length === 0 ? (
                                <div className='flex size-full flex-col items-center justify-center gap-3 py-12'>
                                    <LuCheck
                                        size={64}
                                        className='text-foreground-secondary'
                                    />
                                    <span className='text-foreground-secondary text-lg font-medium'>
                                        <FormattedMessage
                                            id='kiosk.review-recycles.no-recycles'
                                            defaultMessage='No recycles to review'
                                        />
                                    </span>
                                </div>
                            ) : (
                                viewProps.products.map((product) => (
                                    <ProductReviewRow
                                        key={product.productId}
                                        product={product}
                                        onUpdate={(count) => viewProps.updateActualCount(product.productId, count)}
                                    />
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </WizardScreenContainer>
    );
};

export default ReviewRecycles;
